import { memo, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography
} from '@mui/material';
// Our Components
import { AuthContext } from 'context/auth-context';
import { HeaderTableCell } from './HeaderTableCell';
import { TransactionsContext } from 'context/transactions/transactionsContext';
import TransactionDetails from './TransactionDetails';
import TransactionTableRow from './TransactionTableRow';
// Services
import { Businesses } from 'lib/api';
// Utils and Helpers
import { CARD_TRANSACTION_TYPE } from 'helpers/constants';
// Assets
import { ReactComponent as NoTransactionsInfo } from 'assets/transactionsSection/NoTransactionsInfo.svg';
import './TransactionsTable.css';

const statusFormatter = cardTransaction => {
    let transactionTypeName;
    if (cardTransaction?.type === CARD_TRANSACTION_TYPE.SPEND_UPDATE) {
        transactionTypeName = cardTransaction?.type?.split('.')[2];
    } else {
        transactionTypeName = cardTransaction?.type?.split('.')[1];
    }
    const parentTransaction = cardTransaction?.id_parent_card_transaction;

    let label = transactionTypeName;
    if (parentTransaction) {
        label += ` (${'...' + parentTransaction.substring(parentTransaction.length - 6)})`;
    }

    if (cardTransaction?.type === CARD_TRANSACTION_TYPE.DECLINED) {
        return <Chip label={label} className="chip chip-danger fw-bold" />;
    }
    if (cardTransaction?.type === CARD_TRANSACTION_TYPE.REFUND) {
        return <Chip label={label} className="chip chip-warning fw-bold" />;
    }
    if (cardTransaction?.type === CARD_TRANSACTION_TYPE.SPEND) {
        return <Chip label={label} className="chip chip-success fw-bold" />;
    }
    if (cardTransaction?.type === CARD_TRANSACTION_TYPE.SPEND_UPDATE) {
        return <Chip label={label} className="chip chip-success fw-bold" />;
    }
    if (cardTransaction?.type === CARD_TRANSACTION_TYPE.REVERSAL) {
        return <Chip label={label} className="chip chip-danger fw-bold" />;
    }
    if (cardTransaction?.type === CARD_TRANSACTION_TYPE.REPLENISH) {
        return <Chip label={label} className="chip chip-pending fw-bold" />;
    }
};
const TableColumns = [
    { label: 'TXN ID' },
    { label: 'USER' },
    { label: 'MERCHANT' },
    { label: 'TXN CATEGORY' },
    { label: 'DEPARTMENT' },
    { label: 'DATE' },
    { label: 'STATUS' },
    { label: 'AMOUNT' }
];
const TransactionsTable = ({ pagination, setPagination }) => {
    const { businessInfo } = useContext(AuthContext);
    const {
        state: { cardTransactions, shouldUpdateCardTransactionList }
    } = useContext(TransactionsContext);
    const [transactionsCategoriesRaw, setTransactionsCategoriesRaw] = useState([]);
    const [transactionsCategories, setTransactionsCategories] = useState([]);
    const [selectedCardTransactionID, setSelectedCardTransactionID] = useState(null);
    const [isTransactionDetailsOpen, setIsTransactionDetailsOpen] = useState(false);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const cardTransactionId = queryParams.get('cardTransactionId');

    useEffect(() => {
        const fetchTransactionCategories = async () => {
            try {
                const transactionsCategoryList = await Businesses.getTransactionsCategories(businessInfo.id);
                setTransactionsCategoriesRaw(transactionsCategoryList);
                let transactionsCategoryMap = transactionsCategoryList.map(transactionsCategory => ({
                    value: transactionsCategory.name
                }));
                setTransactionsCategories(transactionsCategoryMap);
            } catch (error) {
                console.error(error);
            }
        };
        fetchTransactionCategories();
    }, [businessInfo.id, shouldUpdateCardTransactionList]);

    useEffect(() => {
        if (cardTransactionId) {
            // NOTE: this is a hack because there are several rerenders and
            // the dialog makes the opening animation multiple times
            setTimeout(() => {
                setIsTransactionDetailsOpen(true);
            }, 100);
        }
    }, [cardTransactionId]);

    const handlePageChange = (event, newPage) => {
        setPagination({ ...pagination, page: newPage });
    };

    const handleRowsPerPageChange = event => {
        setPagination({
            page: 0,
            rowsPerPage: Number(event?.target?.value)
        });
    };

    return (
        <>
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            {TableColumns.map((column, index) => (
                                <HeaderTableCell key={index} index value={column.label} position={'left'} />
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ height: !cardTransactions.results.length ? '336px' : 'auto' }}>
                        {cardTransactions.results.map(transaction => (
                            <TransactionTableRow
                                key={transaction.id}
                                transaction={transaction}
                                setSelectedCardTransactionID={setSelectedCardTransactionID}
                                isTransactionDetailsOpen={isTransactionDetailsOpen}
                                setIsTransactionDetailsOpen={setIsTransactionDetailsOpen}
                                statusFormatter={statusFormatter}
                                transactionsCategories={transactionsCategories}
                                transactionsCategoriesRaw={transactionsCategoriesRaw}
                            />
                        ))}
                        {cardTransactions.results.length === 0 && (
                            <TableRow sx={{ alignItems: 'center' }}>
                                <TableCell colSpan={7} sx={{ textAlign: 'center' }}>
                                    <NoTransactionsInfo />
                                    <Typography paragraph className="transactions-table__empty-table-text">
                                        You do not have any Transactions yet!
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 100]}
                component="div"
                count={cardTransactions.total}
                rowsPerPage={pagination.rowsPerPage}
                page={pagination.page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
            />
            {isTransactionDetailsOpen && (
                <TransactionDetails
                    isOpen={isTransactionDetailsOpen}
                    setIsOpen={setIsTransactionDetailsOpen}
                    cardTransactionID={selectedCardTransactionID ? selectedCardTransactionID : cardTransactionId}
                    statusFormatter={statusFormatter}
                    transactionsCategories={transactionsCategories}
                    transactionsCategoriesRaw={transactionsCategoriesRaw}
                />
            )}
        </>
    );
};

export default memo(TransactionsTable);
